"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import { useLightbox } from "./lightbox/LightboxContext";

const ABOUT_IMAGES = [
  "/assets/hero-facade.jpg",
  "/assets/entrance.jpg",
  "/assets/pool-aerial.jpg",
];
const ABOUT_ALT_IDX = [0, 1, 2];

export function About() {
  const t = useTranslations();
  const { open } = useLightbox();
  const swiperRef = useRef<SwiperClass | null>(null);
  const [index, setIndex] = useState(0);

  const galAlts = t.raw("galAlts") as string[];
  const aboutParas = t.raw("aboutParas") as string[];
  const aboutSpecs = t.raw("aboutSpecs") as { label: string; value: string }[];

  const slides = ABOUT_IMAGES.map((src, i) => ({
    src,
    alt: galAlts[ABOUT_ALT_IDX[i]],
  }));

  return (
    <section
      className="mx-auto max-w-[1280px] px-[clamp(20px,4vw,40px)] py-[clamp(72px,9vw,130px)]"
    >
      <div className="grid items-stretch gap-[clamp(40px,5vw,72px)] lg:grid-cols-[1.05fr_0.95fr]">
        <div data-reveal className="min-w-0">
          <p className="mb-5 text-xs uppercase tracking-[.2em] text-accent">
            {t("aboutKicker")}
          </p>
          <h2 className="max-w-[22ch] text-[clamp(28px,3.4vw,44px)] leading-[1.12] text-ink">
            {t("aboutTitle")}
          </h2>
          <div className="mt-[30px] flex max-w-[62ch] flex-col gap-5">
            {aboutParas.map((p) => (
              <p key={p} className="text-[16px] leading-[1.75] font-light text-muted">
                {p}
              </p>
            ))}
          </div>
          <dl className="mt-9 flex flex-col gap-0 border-t border-ink/[.12]">
            {aboutSpecs.map((s) => (
              <div
                key={s.label}
                className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-2 border-b border-ink/[.12] py-3.5"
              >
                <dt className="text-[11.5px] tracking-[.12em] text-subtle uppercase">
                  {s.label}
                </dt>
                <dd className="text-right text-[15px] font-medium leading-[1.4] text-ink">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div data-reveal className="flex min-w-0 flex-col gap-3.5">
          <div className="relative min-h-[320px] flex-1 overflow-hidden border border-ink/10 bg-surface">
            <Swiper
              onSwiper={(s) => (swiperRef.current = s)}
              onSlideChange={(s) => setIndex(s.activeIndex)}
              className="h-full w-full"
            >
              {slides.map((slide) => (
                <SwiperSlide key={slide.src}>
                  <div className="relative h-full w-full">
                    <Image
                      src={slide.src}
                      alt={slide.alt}
                      fill
                      sizes="(min-width: 1024px) 50vw, 100vw"
                      className="object-cover"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <button
              type="button"
              onClick={() => open(slides.map((s) => ({ src: s.src, caption: s.alt })), index)}
              aria-label="Fullscreen"
              className="absolute right-3.5 top-3.5 z-10 flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border-0 bg-ink/70 text-[20px] leading-none text-white transition-colors hover:bg-ink"
            >
              +
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label={t("prev")}
              className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[3px] border border-ink/[.16] bg-none text-[16px] transition-colors hover:border-ink hover:bg-ink hover:text-white"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label={t("next")}
              className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[3px] border border-ink/[.16] bg-none text-[16px] transition-colors hover:border-ink hover:bg-ink hover:text-white"
            >
              →
            </button>
            <p className="ml-auto min-w-0 text-right text-[13px] text-muted">
              {slides[index]?.alt} · {index + 1} / {slides.length}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
